import type { ComponentPropsWithoutRef, Ref } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/**
 * A small count, drawn in the corner of something else (`FE-04.10`;
 * `docs/design/stage2/finding-things.md`'s `Badge` row, `FE-17.2`,
 * `FR-NOTIF-005`).
 *
 * Hidden entirely at 0 rather than rendering "0" — a badge that reads zero is
 * not information, it is clutter that looks like information.
 *
 * `99+` above `max`, and the display cap is deliberately the CLIENT's: the
 * server returns the true count on purpose (`implementation-plan.md` §B17),
 * because capping it server-side would be indistinguishable from a bug at
 * exactly 99.
 *
 * ⚠ **Decorative-by-position and `aria-hidden`, always.** A badge sitting on
 * an icon has no accessible position of its own — the count reaches the
 * member through the accessible name of whatever it sits on (`IconButton`'s
 * `label`, e.g. "Notifications, 3 unread"), which is that caller's job, not
 * this component's. Rendering the count a second time as this element's own
 * accessible name would announce it twice.
 */

const badgeVariants = cva(
  'inline-flex min-w-[1.125rem] items-center justify-center rounded-full bg-accent px-1 text-xs font-medium leading-none text-accent-ink',
);

export interface BadgeProps extends ComponentPropsWithoutRef<'span'> {
  count: number;
  /** @default 99 */
  max?: number;
  ref?: Ref<HTMLSpanElement>;
}

export function Badge({ count, max = 99, className, ref, ...rest }: BadgeProps) {
  if (count <= 0) return null;

  const display = count > max ? `${max}+` : String(count);

  return (
    <span
      ref={ref}
      aria-hidden="true"
      className={cn(badgeVariants(), className)}
      {...rest}
    >
      {display}
    </span>
  );
}
